import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { cn } from '@/lib/utils';
import { Plus, RefreshCw, Search, Fish, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { SpeciesSubNav } from './components/SpeciesNav';
import {
    useCromosAlbumList,
    useUpsertCromosAlbumSpecies,
    useDeleteCromosAlbumSpecies,
    type CromosAlbumRarity,
} from './hooks/useSpeciesAdmin';

const WATER_OPTIONS = [
    { value: 'all', label: 'Todas' },
    { value: 'salada', label: 'Salada' },
    { value: 'dulce', label: 'Dulce' },
] as const;

const RARITY_OPTIONS: CromosAlbumRarity[] = ['legendario', 'épico', 'raro', 'común', 'morralla'];

export function SpeciesCromosAlbumPage() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [waterType, setWaterType] = useState<'all' | 'salada' | 'dulce'>('all');
    const [deleteName, setDeleteName] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [formScientific, setFormScientific] = useState('');
    const [formDisplay, setFormDisplay] = useState('');
    const [formWater, setFormWater] = useState<'salada' | 'dulce'>('salada');
    const [formRarity, setFormRarity] = useState<CromosAlbumRarity>('común');
    const [formError, setFormError] = useState<string | null>(null);

    const { data: entries, isLoading, refetch } = useCromosAlbumList({
        search: search || undefined,
        waterType: waterType === 'all' ? null : waterType,
    });
    const upsertMutation = useUpsertCromosAlbumSpecies();
    const deleteMutation = useDeleteCromosAlbumSpecies();

    const openCreate = () => {
        setFormScientific('');
        setFormDisplay('');
        setFormWater('salada');
        setFormRarity('común');
        setFormError(null);
        setShowForm(true);
    };

    const openEdit = (entry: NonNullable<typeof entries>[number]) => {
        setFormScientific(entry.scientific_name);
        setFormDisplay(entry.display_name);
        setFormWater(entry.water_type);
        setFormRarity(entry.rarity);
        setFormError(null);
        setShowForm(true);
    };

    const handleSave = async () => {
        setFormError(null);
        try {
            await upsertMutation.mutateAsync({
                p_scientific_name: formScientific.trim(),
                p_display_name: formDisplay.trim(),
                p_water_type: formWater,
                p_rarity: formRarity,
            });
            setShowForm(false);
            refetch();
        } catch (e: unknown) {
            setFormError(e instanceof Error ? e.message : 'Error al guardar');
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Álbum Cromos"
                description="Especies del álbum Cromos (species_catalog.is_in_album) — rankings, progreso y hall of fame"
            >
                <div className="flex items-center gap-2">
                    <button
                        onClick={openCreate}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                        <Plus size={14} />
                        Añadir entrada
                    </button>
                    <button
                        onClick={() => refetch()}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50"
                    >
                        <RefreshCw size={14} />
                        Actualizar
                    </button>
                </div>
            </PageHeader>

            <SpeciesSubNav />

            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <div className="relative flex-1 min-w-0 max-w-xs">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Buscar..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                </div>
                <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
                    {WATER_OPTIONS.map(o => (
                        <button
                            key={o.value}
                            onClick={() => setWaterType(o.value)}
                            className={cn(
                                'px-3 py-1.5 rounded-md text-sm font-medium',
                                waterType === o.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                            )}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
                <span className="text-xs text-muted-foreground self-center">
                    {entries?.length ?? 0} entradas
                </span>
            </div>

            {showForm && (
                <div className="rounded-xl border border-border bg-card/50 p-6 space-y-4 max-w-xl">
                    <h3 className="font-semibold text-foreground">
                        {formScientific ? 'Editar entrada Cromos' : 'Nueva entrada Cromos'}
                    </h3>
                    {formError && (
                        <div className="p-2 rounded-lg bg-destructive/10 text-destructive text-sm">{formError}</div>
                    )}
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium mb-1">Nombre científico</label>
                            <input
                                value={formScientific}
                                onChange={e => setFormScientific(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium mb-1">Nombre visible</label>
                            <input
                                value={formDisplay}
                                onChange={e => setFormDisplay(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Agua</label>
                            <select
                                value={formWater}
                                onChange={e => setFormWater(e.target.value as 'salada' | 'dulce')}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                            >
                                <option value="salada">Salada</option>
                                <option value="dulce">Dulce</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Rareza</label>
                            <select
                                value={formRarity}
                                onChange={e => setFormRarity(e.target.value as CromosAlbumRarity)}
                                className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                            >
                                {RARITY_OPTIONS.map(r => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleSave}
                            disabled={upsertMutation.isPending || !formScientific.trim() || !formDisplay.trim()}
                            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground disabled:opacity-50"
                        >
                            Guardar
                        </button>
                        <button
                            onClick={() => setShowForm(false)}
                            className="px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="py-16 text-center text-muted-foreground text-sm">Cargando...</div>
            ) : !entries?.length ? (
                <div className="rounded-xl border border-dashed border-border py-16 text-center">
                    <Fish size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground">No hay entradas</p>
                </div>
            ) : (
                <div className="rounded-xl border border-border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left">
                            <tr>
                                <th className="px-4 py-3 font-medium">Visible</th>
                                <th className="px-4 py-3 font-medium">Científico</th>
                                <th className="px-4 py-3 font-medium">Agua</th>
                                <th className="px-4 py-3 font-medium">Rareza</th>
                                <th className="px-4 py-3 font-medium">Catálogo</th>
                                <th className="px-4 py-3 font-medium w-24">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {entries.map(entry => (
                                <tr key={entry.scientific_name} className="hover:bg-muted/20">
                                    <td className="px-4 py-3 font-medium">{entry.display_name}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{entry.scientific_name}</td>
                                    <td className="px-4 py-3">{entry.water_type}</td>
                                    <td className="px-4 py-3">{entry.rarity}</td>
                                    <td className="px-4 py-3">
                                        {entry.catalog_id ? (
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/species/${entry.catalog_id}`)}
                                                className="inline-flex items-center gap-1 text-primary hover:underline"
                                            >
                                                #{entry.catalog_id}
                                                <ExternalLink size={12} />
                                            </button>
                                        ) : (
                                            <span className="text-amber-600 dark:text-amber-400 text-xs">Sin match</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-1">
                                            <button
                                                type="button"
                                                onClick={() => openEdit(entry)}
                                                className="p-1.5 rounded hover:bg-muted"
                                                title="Editar"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setDeleteName(entry.scientific_name)}
                                                className="p-1.5 rounded hover:bg-muted text-destructive"
                                                title="Eliminar"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <AdminConfirmDialog
                open={deleteName != null}
                variant="danger"
                title="Eliminar del álbum Cromos"
                description={<p>Se quitará de la lista canónica de Cromos. No borra la especie del catálogo general.</p>}
                confirmLabel="Eliminar"
                onCancel={() => setDeleteName(null)}
                onConfirm={() => {
                    if (!deleteName) return;
                    const name = deleteName;
                    setDeleteName(null);
                    deleteMutation.mutate({ p_scientific_name: name }, { onSuccess: () => void refetch() });
                }}
                confirmDisabled={deleteMutation.isPending}
            />
        </div>
    );
}
